import React, { useEffect, useState } from 'react';
import Viewer from 'react-viewer';
import PropTypes from 'prop-types';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

function ScreenshotViewer({
  screenshots, memberId, coverImage, formattedTime, setScreensAreOpen, screensAreOpen,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [images, setImages] = useState(
    coverImage ? [{ src: coverImage, ...screenshots[0] }] : [],
  );
  const [hasLoggedActivity, setHasLoggedActivity] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('[ScreenshotViewer] Initialized:', {
      screenshotCount: screenshots.length,
      hasCoverImage: !!coverImage,
      memberId,
      firstScreenshotUrl: screenshots[0]?.url,
    });
  }, [screenshots, coverImage, memberId]);

  useEffect(() => {
    if (!screenshots.length || !memberId || screenshots.length <= images.length) return;

    const fetchRemainingImages = async () => {
      // If coverImage is not provided, fetch ALL screenshots starting from index 0
      // Otherwise, fetch remaining screenshots starting from index 1
      const startIndex = coverImage ? 1 : 0;

      const urls = await Promise.all(
        screenshots
          .slice(startIndex)
          .filter((screenshot) => screenshot?.url?.trim()) // Only fetch screenshots with valid non-empty url
          .map(async (screenshot, index) => {
            console.log(`[ScreenshotViewer] Fetching screenshot ${startIndex + index}:`, screenshot.url);
            const { success, data } = await sendRequest({
              method: 'GET',
              url: API.MEMBER.screenshot(memberId),
              params: { baseUrl: screenshot.url }, // Don't log activity during URL fetching
            });
            if (!success) {
              console.warn(`[ScreenshotViewer] Failed to fetch screenshot ${startIndex + index}`);
              return null;
            }
            const { url, ...restDetails } = screenshot;
            return { ...restDetails, src: data.url };
          }),
      );

      console.log('[ScreenshotViewer] Fetched screenshots:', urls.filter(Boolean).length);
      setImages((prev) => [...prev, ...urls.filter(Boolean)]);
    };

    setTimeout(fetchRemainingImages, 0);
  }, [screenshots, memberId, coverImage, images.length]);

  // Log activity only when screenshot viewer is opened
  useEffect(() => {
    if (screensAreOpen && !hasLoggedActivity && images.length > 0) {
      const logScreenshotActivity = async () => {
        try {
          const firstScreenshot = screenshots[0];
          if (firstScreenshot?.url) {
            await sendRequest({
              method: 'GET',
              url: API.MEMBER.screenshot(memberId),
              params: {
                baseUrl: firstScreenshot.url,
                logActivity: 'true', // Explicitly request activity logging
              },
            });
            setHasLoggedActivity(true);
          } else {
            console.warn('Cannot log activity: first screenshot has no url', {
              screenshotCount: screenshots.length,
              firstScreenshot: screenshots[0],
            });
          }
        } catch (error) {
          // Ignore logging errors
          console.warn('Failed to log screenshot activity:', error);
        }
      };

      logScreenshotActivity();
    }
  }, [screensAreOpen, hasLoggedActivity, images.length, screenshots, memberId]);

  if (!images.length) return null;

  return (
    <Viewer
      visible={screensAreOpen}
      onClose={() => setScreensAreOpen(false)}
      images={images}
      activeIndex={activeIndex}
      onChange={(item, index) => setActiveIndex(index)}
      zIndex={2000}
      defaultScale={1}
      scalable={false}
      rotatable={false}
      loop={false}
      showTotal={false}
      onMaskClick={() => setScreensAreOpen(false)}
      noImgDetails={`
      ${formattedTime} | 
      Screenshot ${activeIndex + 1} of ${images.length}
      ${images[activeIndex]?.screen && images[activeIndex].screen !== 'Entire screen'
        ? ` | (${images[activeIndex].screen})` : ''}
      `}
    />
  );
}

ScreenshotViewer.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string.isRequired,
  })).isRequired,
  memberId: PropTypes.string.isRequired,
  coverImage: PropTypes.string,
  formattedTime: PropTypes.string,
  setScreensAreOpen: PropTypes.func.isRequired,
  screensAreOpen: PropTypes.bool.isRequired,
};

export default ScreenshotViewer;
